import React, { useCallback } from 'react';
import { Box, DropzoneFileListItem, VisualElementType } from '@cad-arc';
import { AttachmentDto } from '@mark43/cad-api';
import { useDownloadAttachment } from './hooks/use-download-attachment';
import { CombinedMediaItem } from './types';

export interface AttachmentThumbnailItemProps {
    attachmentId: AttachmentDto['attachmentId'];
    attachmentType: AttachmentDto['attachmentType'];
    thumbnailSrc: string;
    showActionsMenu: boolean;
    fileType: CombinedMediaItem['type'];
    fileName: string;
    downloadUrl?: string;
    onOpenPreview?: (attachmentId: number) => void;
    onDelete: (attachmentId: number, attachmentType: AttachmentDto['attachmentType']) => void;
    hideDelete?: boolean;
}

export function AttachmentThumbnailItem({
    attachmentId,
    attachmentType,
    thumbnailSrc,
    showActionsMenu,
    fileType,
    fileName,
    downloadUrl: fileUrl,
    onOpenPreview,
    onDelete,
    hideDelete = false,
}: AttachmentThumbnailItemProps): React.ReactElement {
    const { downloadAttachment } = useDownloadAttachment({
        attachmentId,
        fileName,
        fileUrl,
    });

    const handleDelete = useCallback(
        function handleDeleteAttachment() {
            onDelete(attachmentId, attachmentType);
        },
        [onDelete, attachmentId, attachmentType]
    );

    const getActions = () => {
        if (!showActionsMenu) return;

        const actions: {
            visual: VisualElementType;
            action: () => void;
            label: string;
        }[] = [
            {
                label: 'Download',
                visual: 'Download',
                action: () => {
                    downloadAttachment();
                },
            },
        ];

        if (!hideDelete) {
            actions.push({
                label: 'Delete',
                visual: 'TrashCan' as const,
                action: handleDelete,
            });
        }

        return actions;
    };

    return (
        <Box
            data-testid={`attachment-thumbnail-${attachmentId}`}
            onClick={() => {
                if (onOpenPreview) {
                    onOpenPreview(attachmentId);
                }
            }}
            style={{ cursor: 'pointer' }}
        >
            <DropzoneFileListItem
                variant="thumbnail"
                file={{
                    name: fileName,
                    type: fileType,
                }}
                thumbnailSrc={thumbnailSrc}
                size={showActionsMenu ? 'md' : 'sm'}
                actions={getActions()}
            />
        </Box>
    );
}
